import { Stage } from "../helpers/get-settings";

export type QueryParam = string | string[] | undefined;

export interface UrlProps {
  stage?: Stage;
  requisitionId?: QueryParam;
  applicationId?: QueryParam;
  candidateId?: QueryParam;
  candidateGlobalId?: string;
  sfCandidateId?: QueryParam;
  redirectUrl?: QueryParam;
  scheduleId?: QueryParam;
  cssPage?: QueryParam;
  locale?: QueryParam;
  requisitionCategory?: QueryParam;
  taskType?: QueryParam;
  businessLine?: QueryParam;
}

// ---------------- API ---------------------------
export const BADGE_PHOTO_URL = "/api/badgephotos";
export const DOCUMENT_VALIDATOR_URL = "/api/document-upload/:candidateId";
export const VIDEO_API_URL = "/api/video/:applicationId/:locale/:requisitionCategory";
export const METRIC_API_URL = "/api/metric/";
export const VIDEO_RECORD_API_URL = "/api/video-record/:applicationId";
export const VIDEO_RECORD_NON_ASH_API_URL = "/api/video-record/non-ash/:requisitionId/:candidateId";
export const CAAS_API_URL = "/api/caas/:candidateId?redirectUrl=:redirectUrl";
export const CDS_DOCUMENT_UPLOAD_API_URL = "/api/cds/documentUpload/:candidateId/:applicationId";
export const SDA_API_URL = "/api/sda/:applicationId/:locale";
export const JC_API_URL = "/api/jobcontainer/schedule/:applicationId/:locale?includeStartDate=true";
export const APPLICATION_MANAGE_DATA_API_URL = "/api/application/application-manage-data/:applicationId/:locale";
export const APPLICATION_MANAGE_SF_DATA_API_URL = "/api/salesforce/application-manage-data/:applicationId";
export const SALESFORCE_JOB_DETAILS_URL = "/api/salesforce/jobdetails/:applicationId/:locale";
export const BACKGROUND_CHECK_STATUS_SF_ENDPOINT =
  "/api/salesforce/background-check/candidate/:candidateId/:applicationId";
export const NHE_APPOINTMENT_ENDPOINT = "/api/nhe/appointment/:applicationId";
export const CDS_NATIONAL_ID_API_URL = "/api/cds/candidate/:candidateId";

// ---------------- Internal pages ----------------
export const CHECKLIST_URL = "/checklist/:requisitionId/:applicationId";
export const NEW_HIRE_VIDEO_URL = "/checklist/tasks/new-hire-video/:requisitionId/:applicationId";
export const TAKE_BADGE_PHOTO_START_URL = "/checklist/tasks/badge/:requisitionId/:applicationId/start";
export const TAKE_BADGE_PHOTO_CAMERA_GALLERY_URL =
  "/checklist/tasks/badge/:requisitionId/:applicationId/camera/gallery";
export const TAKE_BADGE_PHOTO_CAMERA_REQUIREMENTS_URL =
  "/checklist/tasks/badge/:requisitionId/:applicationId/camera/requirements";
export const TAKE_BADGE_PHOTO_CAMERA_URL = "/checklist/tasks/badge/:requisitionId/:applicationId/camera";
export const TAKE_BADGE_PHOTO_UPLOAD_REQUIREMENTS_URL =
  "/checklist/tasks/badge/:requisitionId/:applicationId/upload/requirements";
export const TAKE_BADGE_PHOTO_UPLOAD_URL = "/checklist/tasks/badge/:requisitionId/:applicationId/upload";
export const TAKE_BADGE_PHOTO_CONFIRM_URL = "/checklist/tasks/badge/:requisitionId/:applicationId/confirm";
export const TAKE_BADGE_PHOTO_RESULT_URL = "/checklist/tasks/badge/:requisitionId/:applicationId/result";
export const DOCUMENT_UPLOAD_URL = "/checklist/tasks/document/:requisitionId/:applicationId/:documentType/:isEdit";
export const CDS_APPLICATION_ENDPOINT = "/api/cds/candidate/:applicationId";
export const APP_CONFIG_ENDPOINT = "/api/getAppConfig/getASHConfig";
export const BACKGROUND_CHECK_STATUS_ENDPOINT = "/api/status/background-check/candidate/:candidateId/:applicationId";
export const BACKGROUND_CHECK_STATUS_AIR_ENDPOINT =
  "/api/status/background-check/air/candidate/:candidateId/:applicationId";
export const MEDICAL_CHECK_STATUS_ENDPOINT = "/api/status/medical-check/candidate/:candidateId";
export const RIGHT_TO_WORK_STATUS_ENDPOINT = "/api/status/right-to-work/candidate/:candidateId";
export const DRUG_TEST_STATUS_ENDPOINT = "/api/status/drug-test/candidate/:sfCandidateId/businessLine/:businessLine";
export const DRUG_TEST_STATUS_GID_ENDPOINT =
  "/api/status/drug-test/candidateGlobalId/:candidateGlobalId/businessLine/:businessLine";
export const PERSONAL_INFORMATION_COLLECTION_URL = "/checklist/tasks/pic/:requisitionId/:applicationId/poc";
export const BADGE_PHOTO_STATUS_ENDPOINT = "/api/status/badge-photo/candidate/:candidateId";
export const NEW_HIRE_VIDEO_STATUS_ENDPOINT = "/api/status/video/application/:applicationId";
export const TASKSTATUS_ENDPOINT = "/api/taskstatus/:candidateGlobalId";
export const TASK_STATUS_WITH_BBID = "/api/taskstatus/blackbird/:candidateId";
export const POST_TASK_STATUS_WITH_BBID = "/api/taskstatus/blackbird/:candidateId/taskType/:taskType";
export const CS_WITHDRAW_APPLICATION_ENDPOINT = "/api/withdraw-application/:applicationId";
export const DOCUMENT_STATUS_ENDPOINT =
  "/api/document-status/:candidateGlobalId/:applicationId/:locale/Salesforce/False";
export const ONBASE_DOCUMENT_STATUS_ENDPOINT = "/api/status/document-status/:candidateGlobalId/:applicationId/:locale";
export const ATOZ_ENDPOINT = "/api/atoz/:candidateGlobalId/:applicationId/:locale";
export const ATOZ_STATUS_ENDPOINT = "/api/status/atoz-status/:candidateGlobalId/:applicationId/:locale";

// ---------------- External links ----------------
export const USCIS_URL = "https://www.uscis.gov/i-9-central/form-i-9-acceptable-documents";
export const USCIS_URL_ES = "https://www.uscis.gov/es/central-i-9/documentos-aceptables-del-formulario-i-9";
export const I9_ADVANTAGE_URL = "https://hr.i9advantage.com/section1/email/confirm_request.aspx?id=125";
export const I9_INSTRUCTIONS_URL =
  "https://formi9.s3-us-west-2.amazonaws.com/Form-I-9-Instructions/index.html#/lessons/-cLWLtxRjIaerEjxaMOm5cSIbKepwQP4";
export const MX_ACCEPTABLE_DOCUMENTS_URL_EN =
  "https://walpub.s3.us-west-2.amazonaws.com/EmployeeGuides/AMER-LATAM/Mexico/English+Version";
export const MX_ACCEPTABLE_DOCUMENTS_URL_ES =
  "https://walpub.s3.us-west-2.amazonaws.com/EmployeeGuides/AMER-LATAM/Mexico/Spanish+Version.pdf";

export const ZAPPOS_URL = "https://www.zappos.com/newhire";

export const BB_BGC_INFORMATION_COLLECTION_URL: Record<Stage, string> = {
  local:
    "https://beta.devo.jobsatamazon.hvh.a2z.com/application/bgc/?applicationId={applicationId}&jobId={requisitionId}",
  alpha: "",
  "alpha-mx": "",
  beta:
    "https://beta.devo.jobsatamazon.hvh.a2z.com/application/bgc/?applicationId={applicationId}&jobId={requisitionId}",
  "beta-mx": "",
  gamma: "",
  "gamma-mx": "",
  "gamma-uk":
    "https://gamma.devo.jobsatamazon.hvh.a2z.com/application/bgc/?applicationId={applicationId}&jobId={requisitionId}",
  preprod: "",
  "preprod-mx": "",
  "preprod-uk":
    "https://uk.preprod.jobsatamazon.hvh.a2z.com/application/bgc/?applicationId={applicationId}&jobId={requisitionId}",
  prod: "",
  "prod-mx": "",
  "prod-uk": "https://www.jobsatamazon.co.uk/application/bgc/?applicationId={applicationId}&jobId={requisitionId}",
};

export const SCHEDULE_RIGHT_TO_WORK_APPOINTMENT_URL: Record<Stage, string> = {
  //update the right URL
  local: "",
  alpha: "",
  beta: "",
  gamma: "",
  preprod: "",
  prod: "",
  "alpha-mx": "",
  "beta-mx": "",
  "gamma-mx": "",
  "gamma-uk": "",
  "preprod-uk": "",
  "prod-uk": "",
  "preprod-mx": "",
  "prod-mx": "",
};

export const BB_APPOINTMENT_DETAIL_PAGE_URL: Record<Stage, string> = {
  local:
    "https://beta-us.devo.jobsatamazon.hvh.a2z.com/application/?page=appointment-detail-page&requisitionId={requisitionId}&applicationId={applicationId}",
  alpha:
    "https://beta-us.devo.jobsatamazon.hvh.a2z.com/application/?page=appointment-detail-page&requisitionId={requisitionId}&applicationId={applicationId}",
  "alpha-mx":
    "https://beta-us.devo.jobsatamazon.hvh.a2z.com/application/?page=appointment-detail-page&requisitionId={requisitionId}&applicationId={applicationId}", //Placeholder
  beta:
    "https://beta-us.devo.jobsatamazon.hvh.a2z.com/application/?page=appointment-detail-page&requisitionId={requisitionId}&applicationId={applicationId}",
  "beta-mx":
    "https://beta-us.devo.jobsatamazon.hvh.a2z.com/application/?page=appointment-detail-page&requisitionId={requisitionId}&applicationId={applicationId}", //Placeholder
  gamma:
    "https://gamma-us.devo.jobsatamazon.hvh.a2z.com/application/?page=appointment-detail-page&requisitionId={requisitionId}&applicationId={applicationId}",
  "gamma-mx":
    "https://gamma-mx.devo.jobsatamazon.hvh.a2z.com/application/?page=appointment-detail-page&requisitionId={requisitionId}&applicationId={applicationId}",
  "gamma-uk":
    "https://gamma.devo.jobsatamazon.hvh.a2z.com/application/?page=appointment-detail-page&requisitionId={requisitionId}&applicationId={applicationId}",
  preprod:
    "https://us.preprod.jobsatamazon.hvh.a2z.com/application/?page=appointment-detail-page&requisitionId={requisitionId}&applicationId={applicationId}",
  "preprod-mx":
    "https://mx.preprod.jobsatamazon.hvh.a2z.com/application/?page=appointment-detail-page&requisitionId={requisitionId}&applicationId={applicationId}",
  "preprod-uk":
    "https://uk.preprod.jobsatamazon.hvh.a2z.com/application/?page=appointment-detail-page&requisitionId={requisitionId}&applicationId={applicationId}",
  prod:
    "https://hiring.amazon.com/application/?page=appointment-detail-page&requisitionId={requisitionId}&applicationId={applicationId}",
  "prod-mx":
    "https://hiring.amazon.com/application/?page=appointment-detail-page&requisitionId={requisitionId}&applicationId={applicationId}", //Placeholder
  "prod-uk":
    "https://www.jobsatamazon.co.uk/application/?page=appointment-detail-page&requisitionId={requisitionId}&applicationId={applicationId}",
};

export const CSS_PAGE_URL: Record<Stage, string> = {
  local:
    "https://beta-us.devo.jobsatamazon.hvh.a2z.com/selfservice/schedule/current-schedule/{applicationId}/{requisitionId}",
  alpha:
    "https://beta-us.devo.jobsatamazon.hvh.a2z.com/selfservice/schedule/current-schedule/{applicationId}/{requisitionId}",
  "alpha-mx":
    "https://beta-mx.devo.jobsatamazon.hvh.a2z.com/selfservice/schedule/current-schedule/{applicationId}/{requisitionId}",
  beta:
    "https://beta-us.devo.jobsatamazon.hvh.a2z.com/selfservice/schedule/current-schedule/{applicationId}/{requisitionId}",
  "beta-mx":
    "https://beta-mx.devo.jobsatamazon.hvh.a2z.com/selfservice/schedule/current-schedule/{applicationId}/{requisitionId}",
  gamma:
    "https://gamma-us.devo.jobsatamazon.hvh.a2z.com/selfservice/schedule/current-schedule/{applicationId}/{requisitionId}",
  "gamma-mx":
    "https://gamma-mx.devo.jobsatamazon.hvh.a2z.com/selfservice/schedule/current-schedule/{applicationId}/{requisitionId}",
  "gamma-uk":
    "https://gamma.devo.jobsatamazon.hvh.a2z.com/selfservice/schedule/current-schedule/{applicationId}/{requisitionId}",
  preprod:
    "https://us.preprod.jobsatamazon.hvh.a2z.com/selfservice/schedule/current-schedule/{applicationId}/{requisitionId}",
  "preprod-mx":
    "https://mx.preprod.jobsatamazon.hvh.a2z.com/selfservice/schedule/current-schedule/{applicationId}/{requisitionId}",
  "preprod-uk":
    "https://uk.preprod.jobsatamazon.hvh.a2z.com/selfservice/schedule/current-schedule/{applicationId}/{requisitionId}",
  prod: "https://hiring.amazon.com/selfservice/schedule/current-schedule/{applicationId}/{requisitionId}",
  "prod-mx": "https://trabaja.amazon.com.mx/selfservice/schedule/current-schedule/{applicationId}/{requisitionId}",
  "prod-uk": "https://www.jobsatamazon.co.uk/selfservice/schedule/current-schedule/{applicationId}/{requisitionId}",
};

export const CSS_MY_JOBS_URL: Record<Stage, string> = {
  local: "https://beta-us.devo.jobsatamazon.hvh.a2z.com/app#/myApplications",
  alpha: "https://beta-us.devo.jobsatamazon.hvh.a2z.com/app#/myApplications",
  "alpha-mx": "https://beta-mx.devo.jobsatamazon.hvh.a2z.com/app#/myApplications",
  beta: "https://beta-us.devo.jobsatamazon.hvh.a2z.com/app#/myApplications",
  "beta-mx": "https://beta-mx.devo.jobsatamazon.hvh.a2z.com/app#/myApplications",
  gamma: "https://gamma-us.devo.jobsatamazon.hvh.a2z.com/app#/myApplications",
  "gamma-mx": "https://gamma-mx.devo.jobsatamazon.hvh.a2z.com/app#/myApplications",
  "gamma-uk": "https://gamma.devo.jobsatamazon.hvh.a2z.com/app#/myApplications",
  preprod: "https://us.preprod.jobsatamazon.hvh.a2z.com/app#/myApplications",
  "preprod-mx": "https://mx.preprod.jobsatamazon.hvh.a2z.com/app#/myApplications",
  "preprod-uk": "https://uk.preprod.jobsatamazon.hvh.a2z.com/app#/myApplications",
  prod: "https://hiring.amazon.com/app#/myApplications",
  "prod-mx": "https://trabaja.amazon.com.mx/app#/myApplications",
  "prod-uk": "https://www.jobsatamazon.co.uk/app#/myApplications",
};

export const APPOINTMENT_SELF_SERVICE_URL: Record<Stage, string> = {
  local: "https://beta.pdx.css.associate.amazondelivers.jobs/selfservice/appointment/{applicationId}/{requisitionId}",
  alpha: "https://beta.pdx.css.associate.amazondelivers.jobs/selfservice/appointment/{applicationId}/{requisitionId}",
  "alpha-mx":
    "https://beta.pdx.css.associate.amazondelivers.jobs/selfservice/appointment/{applicationId}/{requisitionId}",
  beta: "https://beta.pdx.css.associate.amazondelivers.jobs/selfservice/appointment/{applicationId}/{requisitionId}",
  "beta-mx":
    "https://beta.pdx.css.associate.amazondelivers.jobs/selfservice/appointment/{applicationId}/{requisitionId}",
  gamma: "https://gamma.iad.css.associate.amazondelivers.jobs/selfservice/appointment/{applicationId}/{requisitionId}",
  "gamma-mx":
    "https://gamma-mx.iad.css.associate.amazondelivers.jobs/selfservice/appointment/{applicationId}/{requisitionId}",
  "gamma-uk":
    "https://gamma-eu.dub.css.associate.amazondelivers.jobs/selfservice/appointment/{applicationId}/{requisitionId}",
  preprod:
    "https://preprod-us.iad.css.associate.amazondelivers.jobs/selfservice/appointment/{applicationId}/{requisitionId}",
  "preprod-mx":
    "https://preprod-mx.iad.css.associate.amazondelivers.jobs/selfservice/appointment/{applicationId}/{requisitionId}",
  "preprod-uk":
    "https://preprod-eu.dub.css.associate.amazondelivers.jobs/selfservice/appointment/{applicationId}/{requisitionId}",
  prod: "https://prod-us.iad.css.associate.amazondelivers.jobs/selfservice/appointment/{applicationId}/{requisitionId}",
  "prod-mx":
    "https://prod-mx.iad.css.associate.amazondelivers.jobs/selfservice/appointment/{applicationId}/{requisitionId}",
  "prod-uk":
    "https://prod-eu.dub.css.associate.amazondelivers.jobs/selfservice/appointment/{applicationId}/{requisitionId}",
};

export const ADOBE_DATA_LAYER_URL = {
  local: "https://assets.adobedtm.com/launch-EN2c38d445ba884f1ca9901284481b2c7b-staging.min.js",
  alpha: "https://assets.adobedtm.com/launch-EN2c38d445ba884f1ca9901284481b2c7b-staging.min.js",
  "alpha-mx": "https://assets.adobedtm.com/launch-EN2c38d445ba884f1ca9901284481b2c7b-staging.min.js", //Placeholder
  beta: "https://assets.adobedtm.com/launch-EN2c38d445ba884f1ca9901284481b2c7b-staging.min.js",
  "beta-mx": "https://assets.adobedtm.com/launch-EN2c38d445ba884f1ca9901284481b2c7b-staging.min.js", //Placeholder
  gamma: "https://assets.adobedtm.com/launch-EN2c38d445ba884f1ca9901284481b2c7b-staging.min.js",
  "gamma-mx": "https://assets.adobedtm.com/launch-EN2c38d445ba884f1ca9901284481b2c7b-staging.min.js",
  "gamma-uk": "https://assets.adobedtm.com/launch-EN2c38d445ba884f1ca9901284481b2c7b-staging.min.js", //placeholder
  preprod: "https://assets.adobedtm.com/launch-EN2c38d445ba884f1ca9901284481b2c7b-staging.min.js",
  "preprod-mx": "https://assets.adobedtm.com/launch-EN2c38d445ba884f1ca9901284481b2c7b-staging.min.js",
  "preprod-uk": "https://assets.adobedtm.com/launch-EN2c38d445ba884f1ca9901284481b2c7b-staging.min.js", //placeholder
  prod: "https://assets.adobedtm.com/launch-EN6bc43e7eee5146beaa43d868366aabe6.min.js",
  "prod-mx": "https://assets.adobedtm.com/launch-EN6bc43e7eee5146beaa43d868366aabe6.min.js", //Placeholder
  "prod-uk": "https://assets.adobedtm.com/launch-EN6bc43e7eee5146beaa43d868366aabe6.min.js", //Placeholder
};

export const CONTACT_US: Record<Stage, string> = {
  local: "https://beta-us.devo.jobsatamazon.hvh.a2z.com/contact-us",
  alpha: "https://beta-us.devo.jobsatamazon.hvh.a2z.com/contact-us",
  "alpha-mx": "https://beta-mx.devo.jobsatamazon.hvh.a2z.com/contact-us",
  beta: "https://beta-us.devo.jobsatamazon.hvh.a2z.com/contact-us",
  "beta-mx": "https://beta-mx.devo.jobsatamazon.hvh.a2z.com/contact-us",
  gamma: "https://gamma-us.devo.jobsatamazon.hvh.a2z.com/contact-us",
  "gamma-mx": "https://gamma-mx.devo.jobsatamazon.hvh.a2z.com/contact-us",
  "gamma-uk": "https://gamma.devo.jobsatamazon.hvh.a2z.com/contact-us",
  preprod: "https://us.preprod.jobsatamazon.hvh.a2z.com/contact-us",
  "preprod-mx": "https://mx.preprod.jobsatamazon.hvh.a2z.com/contact-us",
  "preprod-uk": "https://uk.preprod.jobsatamazon.hvh.a2z.com/contact-us",
  prod: "https://hiring.amazon.com/contact-us",
  "prod-mx": "https://trabaja.amazon.com.mx/contact-us",
  "prod-uk": "https://www.jobsatamazon.co.uk/contact-us",
};

const KNOWN_PARAMETERS = [
  "requisitionId",
  "applicationId",
  "candidateId",
  "sfCandidateId",
  "redirectUrl",
  "scheduleId",
  "cssPage",
  "locale",
  "requisitionCategory",
  "taskType",
  "businessLine",
  "candidateGlobalId",
];

export const buildUrl = (url: string | Record<Stage, string>, { stage, ...params }: UrlProps): string => {
  const href = typeof url === "string" ? url : url[stage || ("local" as Stage)];
  return KNOWN_PARAMETERS.reduce((acc, paramName) => {
    const value = (params as any)[paramName];
    if (!value) {
      return acc;
    }
    return acc.replace(`:${paramName}`, value).replace(`{${paramName}}`, value);
  }, href);
};
